import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Menu,
  OutlinedInput,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import { ReactComponent as DropdownIcon } from "../../vendor/_new/icon/navbar/dropdown.svg";
import { ReactComponent as DropupIcon } from "../../vendor/_new/icon/arrow_up.svg";
import { ReactComponent as SearchIcon } from "../../vendor/_new/icon/navbar/search.svg";
import { ReactComponent as PlusIcon } from "../../vendor/_new/icon/sites/plus.svg";

import colors from "../../utils/colors";
import useGlobalSite from "../../hooks/user/useGlobalSite";
import { ALL_SITES_ID, isAllSites } from "../../utils/globalSite";

const siteMenuRowSx = (width, height = 32) => ({
  width,
  height,
  p: 2,
  cursor: "pointer",
  borderRadius: "8px",
  "&:hover": {
    backgroundColor: colors.neutral[50],
  },
  "& .site-row-globe": {
    color: colors.neutral[600],
  },
});

function SiteMenuRow({ active, locked, onClick, label }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      onClick={locked ? undefined : onClick}
      sx={{
        ...siteMenuRowSx("100%"),
        opacity: locked ? 0.65 : 1,
        cursor: locked ? "default" : "pointer",
        ...(active && { backgroundColor: colors.primary.alpha }),
        ...(locked && { "&:hover": { backgroundColor: active ? colors.primary.alpha : "transparent" } }),
        "& .site-row-globe": {
          color: active ? colors.primary[300] : colors.neutral[600],
        },
      }}
    >
      <LanguageOutlinedIcon className="site-row-globe" sx={{ fontSize: 16, flexShrink: 0 }} />
      <Typography
        sx={{
          fontSize: 14,
          lineHeight: "20px",
          pl: 1,
          color: active ? colors.primary[300] : colors.neutral[700],
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        noWrap
      >
        {label}
      </Typography>
    </Stack>
  );
}

export default function GlobalSiteSelector({
  enabled: enabledProp,
  showAllOption = true,
}) {
  const navigate = useNavigate();
  const {
    curSiteId,
    setCurSiteId,
    siteSelectorEnabled,
    siteSelectorShowAllOption,
    siteOptions,
    projectList,
    sitesReady,
  } = useGlobalSite();

  const enabled = enabledProp !== undefined ? enabledProp : siteSelectorEnabled;
  const locked = !enabled;
  const showAll =
    showAllOption !== undefined ? showAllOption : siteSelectorShowAllOption;

  const [anchorEl, setAnchorEl] = useState(null);
  const [pattern, setPattern] = useState("");
  const open = Boolean(anchorEl);

  const pillLabel = isAllSites(curSiteId) ? "All sites" : curSiteId;

  const filteredSites = useMemo(() => {
    const q = pattern.trim().toLowerCase();
    if (!q) return siteOptions;
    return siteOptions.filter((s) => (s?.site_id || "").toLowerCase().includes(q));
  }, [siteOptions, pattern]);

  const sitesLoading = !sitesReady;
  const searchQuery = pattern.trim();
  const showEmptySitesMessage = sitesReady && filteredSites.length === 0;
  const emptySitesMessage = searchQuery
    ? "No sites match your search"
    : "No sites in this project";

  useEffect(() => () => {
    setAnchorEl(null);
  }, []);

  if (projectList === null) {
    return null;
  }

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setPattern("");
  };

  const handleSelect = (siteId) => {
    if (locked) return;
    setCurSiteId(siteId);
    handleClose();
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        onClick={handleOpen}
        sx={{
          width: 160,
          minWidth: 160,
          maxWidth: 160,
          height: 32,
          borderRadius: "8px",
          py: 1,
          px: 2,
          cursor: "pointer",
          opacity: locked ? 0.9 : 1,
          backgroundColor: open ? colors.primary.alpha : "transparent",
          "& svg.site-chevron": {
            color: colors.primary[300],
            flexShrink: 0,
          },
          "& svg.site-globe": {
            color: colors.primary[300],
            flexShrink: 0,
          },
          "& .MuiTypography-root": {
            color: colors.primary[300],
          },
          "&:hover": {
            backgroundColor: colors.primary.alpha,
          },
        }}
      >
        {locked ? (
          <LockOutlinedIcon className="site-globe" sx={{ fontSize: 16, flexShrink: 0 }} />
        ) : (
          <LanguageOutlinedIcon className="site-globe" sx={{ fontSize: 16, flexShrink: 0 }} />
        )}
        <Typography
          noWrap
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            lineHeight: "21px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {pillLabel}
        </Typography>
        {open ? <DropupIcon className="site-chevron" /> : <DropdownIcon className="site-chevron" />}
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 10,
          left: 0,
          "& .MuiMenu-paper": { boxShadow: "none" },
          "& .MuiList-root": {
            px: 3,
            py: 3,
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
          },
        }}
      >
        <Box sx={{ width: 300 }}>
          <Stack spacing={3}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
              <Typography sx={{
                fontSize: 14,
                lineHeight: "21px",
                fontWeight: "medium",
                color: colors.neutral[1000],
              }}>
                {locked ? "Site scope (All sites)" : "Site scope"}
              </Typography>
              {!locked && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  onClick={() => {
                    navigate("/v2/sites/new");
                    handleClose();
                  }}
                  sx={{
                    backgroundColor: colors.primary.alpha,
                    borderRadius: "8px",
                    height: 26,
                    px: 3,
                    cursor: "pointer",
                  }}
                >
                  <PlusIcon style={{ color: colors.primary[300] }} />
                  <Typography sx={ { fontSize: 13, lineHeight: "18px", color: colors.primary[300] }}>
                    Add site
                  </Typography>
                </Stack>
              )}
            </Stack>
            {locked && (
              <Typography sx={ { fontSize: 12, color: colors.neutral[600], mt: -1 }}>
                Site scope is fixed to All sites on this page. You can browse sites below.
              </Typography>
            )}
            <OutlinedInput
              startAdornment={<SearchIcon style={{ color: colors.neutral[600] }} />}
              placeholder="Search sites"
              fullWidth
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              sx={{
                height: 32,
                borderRadius: "8px",
                fontSize: 14,
                lineHeight: "21px",
                borderColor: colors.neutral[600],
                pl: 2,
                "& .MuiOutlinedInput-input": {
                  fontSize: 14,
                  lineHeight: "21px",
                  px: 2,
                },
                "& .MuiInputBase-input::placeholder": {
                  fontSize: 14,
                  lineHeight: "21px",
                },
              }}
            />
            {sitesLoading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ width: "100%", height: 200 }}>
                <CircularProgress sx={{ color: colors.neutral[400] }} />
              </Stack>
            ) : (
              <ReactPerfectScrollbar style={{ maxHeight: "240px" }}>
                <Stack>
                  {showAll && (
                    <SiteMenuRow
                      label="All sites"
                      active={isAllSites(curSiteId)}
                      locked={locked}
                      onClick={() => handleSelect(ALL_SITES_ID)}
                    />
                  )}
                  {filteredSites.map((site) => {
                    const active = !locked && !isAllSites(curSiteId) && curSiteId === site.site_id;
                    return (
                      <SiteMenuRow
                        key={site.site_id}
                        label={site.site_id}
                        active={active}
                        locked={locked}
                        onClick={() => handleSelect(site.site_id)}
                      />
                    );
                  })}
                  {showEmptySitesMessage && (
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "21px",
                        py: 2,
                        pl: showAll ? 2 : 0,
                        color: colors.neutral[600],
                      }}
                    >
                      {emptySitesMessage}
                    </Typography>
                  )}
                </Stack>
              </ReactPerfectScrollbar>
            )}
          </Stack>
        </Box>
      </Menu>
    </>
  );
}
